/// <amd-module name="Wit/WorkItemDataManager/WorkItemDataManager" />
import { getTFSProject } from "Tfs/Platform/Page";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssPerformanceService, IVssScenario } from "VSS/Platform/Performance";
import { traceWarning } from "VSS/Platform/Trace";
import { WITCommonConstants } from "Wit/Common/Generated/Constants";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { QueryErrorPolicy, WorkItemClassificationNode } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { WorkItemIndexDBCacheManager } from "Wit/WorkItemDataManager/CacheManagers/WorkItemIndexDBCacheManager";
import { WorkItemMetadataCacheInformationManager } from "Wit/WorkItemDataManager/CacheManagers/WorkItemMetadataCacheInformationManager";
import { WorkItemMetadataCacheStampManager } from "Wit/WorkItemDataManager/CacheManagers/WorkItemMetadataCacheStampManager";
import { IFieldProjectData, ILinkTypes, IWorkItemData, IWorkItemDataSource, IWorkItemTypeData } from "Wit/WorkItemDataManager/Contracts";
import { getDataSources } from "Wit/WorkItemDataManager/Sources";

export interface IWorkItemDataManager extends IVssService {
    beginGetWorkItemData(
        ids: number[],
        isDeleted?: boolean,
        includeInRecentActivity?: boolean,
        includeHistory?: boolean,
        errorPolicy?: QueryErrorPolicy
    ): Promise<IWorkItemData[] | null>;

    beginGetLinkTypes(): Promise<ILinkTypes | null>;

    beginGetConstantSets(ids: string[]): Promise<{ [key: string]: string[] } | null>;

    beginGetFieldProjectData(projectIdOrName: string): Promise<IFieldProjectData | null>;

    beginGetFields(): Promise<IFieldEntry[] | null>;

    beginGetNodes(projectId: string): Promise<WorkItemClassificationNode[] | null>;

    beginGetWorkItemTypeData(projectId: string, typeNames: string[]): Promise<IWorkItemTypeData[] | null>;
}

class WorkItemDataManager extends VssService implements IWorkItemDataManager {
    private _dataSources: IWorkItemDataSource[];
    private _projectId: string;
    private _cacheStampManager: WorkItemMetadataCacheStampManager;
    private _workItemIndexDBCacheManager: WorkItemIndexDBCacheManager;
    private _cacheInformationManager: WorkItemMetadataCacheInformationManager;

    public _serviceStart(pageContext: IVssPageContext) {
        this.pageContext = pageContext;
        this._cacheStampManager = new WorkItemMetadataCacheStampManager(pageContext);
        this._workItemIndexDBCacheManager = new WorkItemIndexDBCacheManager(pageContext);
        this._cacheInformationManager = new WorkItemMetadataCacheInformationManager(pageContext);
        this._dataSources = getDataSources(pageContext, this._cacheInformationManager, this._cacheStampManager);
        this._projectId = getTFSProject(pageContext)?.id || "";
    }

    public async beginGetWorkItemData(
        ids: number[],
        isDeleted?: boolean,
        includeInRecentActivity?: boolean,
        includeHistory?: boolean,
        errorPolicy?: QueryErrorPolicy
    ): Promise<IWorkItemData[] | null> {
        const performanceService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        const perfScenario: IVssScenario = performanceService.startScenario("widm-get-work-item-data", false);

        // Data for an actual work item should never be cached, just call the data sources
        // First try to get data from cache if not present then get it from source.
        const data = await this._getDataFromSource<IWorkItemData[] | null>(async (ds: IWorkItemDataSource) => {
            return await ds.beginGetWorkItemData(ids, isDeleted, includeInRecentActivity, includeHistory, errorPolicy);
        });

        performanceService.endScenario(WITCommonConstants.WorkItemTrackingArea, perfScenario.scenarioName, false);
        return data;
    }

    public async beginGetLinkTypes(): Promise<ILinkTypes | null> {
        const performanceService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        const perfScenario: IVssScenario = performanceService.startScenario("widm-get-link-types", false);

        const types = await this._getDataWithCache<ILinkTypes | null>(this._projectId, WITCommonConstants.LinkTypes, "_", dataSource =>
            dataSource.beginGetLinkTypes()
        );

        performanceService.endScenario(WITCommonConstants.WorkItemTrackingArea, perfScenario.scenarioName, false);
        return types;
    }

    public async beginGetConstantSets(ids: string[]): Promise<{ [key: string]: string[] } | null> {
        const performanceService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        const perfScenario: IVssScenario = performanceService.startScenario("widm-get-constant-sets", false);

        const key = this._cacheStampManager.makeCacheKey(ids);

        const constantSets = await this._getDataWithCache<{ [key: string]: string[] } | null>(
            this._projectId,
            WITCommonConstants.ConstantSets,
            key,
            dataSource => dataSource.beginGetConstantSets(ids)
        );

        performanceService.endScenario(WITCommonConstants.WorkItemTrackingArea, perfScenario.scenarioName, false);
        return constantSets;
    }

    public async beginGetFieldProjectData(projectId: string): Promise<IFieldProjectData | null> {
        const performanceService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        const perfScenario: IVssScenario = performanceService.startScenario("widm-get-field-project-data", false);

        const data = await this._getDataWithCache<IFieldProjectData | null>(projectId, WITCommonConstants.TeamProjects, projectId, dataSource =>
            dataSource.beginGetFieldProjectData(projectId)
        );

        performanceService.endScenario(WITCommonConstants.WorkItemTrackingArea, perfScenario.scenarioName, false);
        return data;
    }

    public async beginGetFields(): Promise<IFieldEntry[] | null> {
        const performanceService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        const perfScenario: IVssScenario = performanceService.startScenario("widm-get-fields", false);
        const data = await this._getDataWithCache<IFieldEntry[] | null>(this._projectId, WITCommonConstants.Fields, "_", dataSource =>
            dataSource.beginGetFields()
        );

        performanceService.endScenario(WITCommonConstants.WorkItemTrackingArea, perfScenario.scenarioName, false);
        return data;
    }

    public async beginGetNodes(projectId: string): Promise<WorkItemClassificationNode[] | null> {
        const performanceService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        const perfScenario: IVssScenario = performanceService.startScenario("widm-get-nodes", false);
        const data = await this._getDataWithCache<WorkItemClassificationNode[] | null>(projectId, WITCommonConstants.Nodes, "_", dataSource => {
            if (dataSource.beginGetNodes) {
                return dataSource.beginGetNodes(projectId);
            }
            return Promise.resolve(null);
        });

        performanceService.endScenario(WITCommonConstants.WorkItemTrackingArea, perfScenario.scenarioName, false);
        return data;
    }

    public async beginGetWorkItemTypeData(projectId: string, typeNames: string[]): Promise<IWorkItemTypeData[] | null> {
        const performanceService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        const perfScenario: IVssScenario = performanceService.startScenario("widm-get-work-item-type-data", false);
        if (typeNames.length === 1) {
            performanceService.endScenario(WITCommonConstants.WorkItemTrackingArea, perfScenario.scenarioName, false);
            return this._getDataWithCache(projectId, WITCommonConstants.WorkItemTypes, typeNames[0], dataSource =>
                dataSource.beginGetWorkItemTypeData(projectId, typeNames)
            );
        }

        const workItemTypes = await this._getDataFromSource<IWorkItemTypeData[] | null>(ds => ds.beginGetWorkItemTypeData(projectId, typeNames));
        const stamp = await this._cacheStampManager.getMetadataCacheStamp(WITCommonConstants.WorkItemTypes);
        workItemTypes &&
            workItemTypes.map(async workItemType => {
                try {
                    const cachedKeys = await this._workItemIndexDBCacheManager.cacheData(
                        projectId,
                        WITCommonConstants.WorkItemTypes,
                        this._transformKey(workItemType.name),
                        [workItemType],
                        stamp
                    );
                    if (cachedKeys) {
                        // Update cookie with new cache stamp
                        this._cacheInformationManager.persist(projectId, WITCommonConstants.WorkItemTypes, cachedKeys, stamp);
                    }
                } catch (reason) {
                    // Do not fail when caching fails
                    traceWarning(this.pageContext, WITCommonConstants.WorkItemTrackingArea, "WorkItemType.CacheResults", reason);
                }
            });

        performanceService.endScenario(WITCommonConstants.WorkItemTrackingArea, perfScenario.scenarioName, false);
        return workItemTypes ?? [];
    }

    private async _getDataWithCache<T>(
        scopeId: string,
        type: string,
        key: string,
        getData: (dataSource: IWorkItemDataSource) => Promise<T>
    ): Promise<T | null> {
        key = this._transformKey(key);

        const stamp = await this._cacheStampManager.getMetadataCacheStamp(type);

        let data;
        let resolvedFromCache;

        if (stamp) {
            data = await this._workItemIndexDBCacheManager.getDataFromCache<T>(scopeId, type, key, stamp);
        }

        if (!data) {
            data = await this._getDataFromSource<T>(getData);
            resolvedFromCache = false;
        } else {
            resolvedFromCache = true;
        }

        if (data && !resolvedFromCache) {
            const cachedKeys = await this._workItemIndexDBCacheManager.cacheData(scopeId, type, key, data, stamp);

            if (cachedKeys && this._cacheInformationManager) {
                // Update cookie with new cache stamp
                this._cacheInformationManager.persist(scopeId, type, cachedKeys, stamp);
            }
        }

        return data;
    }

    private async _getDataFromSource<T>(getData: (dataSource: IWorkItemDataSource) => Promise<T>, dataSourceIndex: number = 0): Promise<T | null> {
        const continueWithNextDataSource = () => {
            // Try the next data source, if there is one
            if (dataSourceIndex + 1 < this._dataSources.length) {
                return this._getDataFromSource(getData, dataSourceIndex + 1);
            }

            return null;
        };

        const dataFromSource = getData(this._dataSources[dataSourceIndex]);
        if (!dataFromSource) {
            return continueWithNextDataSource();
        } else {
            const data = await dataFromSource;
            if (!data) {
                return continueWithNextDataSource();
            }

            return data;
        }
    }

    private _transformKey(key: string): string {
        return key.toLowerCase();
    }
}

const serviceName = "IWorkItemDataManager";
Services.add(serviceName, { serviceFactory: WorkItemDataManager });
